import dayjs from 'dayjs'

import type {
  ActivitySegmentChangedAttribute,
  ActivitySegmentRule,
  SegmentRuleScalar,
  SegmentRuleValue
} from '@/types/customer/ActivitiesFeed'
import { uncapitalize } from '@/utils/string'

export const SEGMENT_DETAILS_I18N_PREFIX = 'pages.customer.customerDetailPage.blocks.activity.segment.details'

const AUDIENCE_BLOCK_I18N_PREFIX = 'pages.audience.audienceCreatePage.block'
const AUDIENCE_OPERATOR_I18N_PREFIX = 'pages.audience.operators'

export type Translate = (key: string, params?: Record<string, unknown>) => string

export interface SegmentCondition {
  key: string
  label: string
  operator: string
  value: string
}

export interface SegmentChange {
  key: string
  label: string
  before: string
  after: string
}

const EMPTY_VALUE = '—'

const DATE_ATTRIBUTES = ['last_valid_checkin', 'registration_date', 'birth_date']

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/
const DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2}[T ]/

// Segment attributes are worded exactly like the audience builder, reusing its keys so both
// screens name a rule the same way. Slugs with no audience wording fall back to the raw slug.
const AUDIENCE_LABEL_KEYS: Record<string, string> = {
  age: 'profil.age',
  average_checkin_count: 'visit.averageCheckinCount',
  birth_date: 'profil.birthDate',
  can_receive_push_notifications: 'engagement.walletInstalled',
  checkin_count: 'visit.checkinCount',
  city: 'profil.city',
  consumed_product: 'consumedProduct.product',
  country: 'profil.country',
  days_since_last_checkin: 'visit.daysSinceLastCheckin',
  days_since_register: 'visit.daysSinceRegistration',
  email: 'profil.email',
  first_name: 'profil.firstName',
  gender: 'profil.genders',
  has_optin_email: 'profil.hasOptinEmail',
  last_name: 'profil.lastName',
  last_valid_checkin: 'visit.lastValidCheckin',
  optin: 'profil.optin',
  point_cumulated: 'engagement.pointCumulated',
  point_total: 'engagement.pointTotal',
  registration_date: 'visit.registrationDate',
  tags: 'visit.tags',
  webapp_log_in: 'engagement.webappConnection'
}

const booleanLeaf =
  (truthy: string, falsy: string, unknown?: string) =>
  (value: SegmentRuleScalar): string | null => {
    if (value === null || value === undefined || value === '') return unknown ?? null
    if (value === true || value === 1 || value === 'true' || value === '1') return truthy
    if (value === false || value === 0 || value === 'false' || value === '0') return falsy

    return null
  }

// Enum and boolean rule values are worded by the audience builder too, so a consent rule reads
// "Consentement SMS est Oui" here exactly as the audience recap spells it out.
const AUDIENCE_VALUE_KEYS: Record<string, { prefix: string; leaf: (value: SegmentRuleScalar) => string | null }> = {
  can_receive_push_notifications: { prefix: 'engagement.walletInstalled', leaf: booleanLeaf('yes', 'no') },
  gender: { prefix: 'profil.genders', leaf: (value) => (value === null || value === '' ? 'unknown' : String(value)) },
  has_optin_email: { prefix: 'profil.optin', leaf: booleanLeaf('true', 'false', 'null') },
  optin: { prefix: 'profil.optin', leaf: booleanLeaf('true', 'false', 'null') },
  webapp_log_in: { prefix: 'engagement.webappConnection', leaf: booleanLeaf('yes', 'no') }
}

const isDateAttribute = (attribute: string): boolean => DATE_ATTRIBUTES.includes(attribute)

// Rule values are stored as JSON arrays, even when the rule holds a single value.
const unwrap = (value: SegmentRuleValue): SegmentRuleValue =>
  Array.isArray(value) && value.length === 1 ? value[0] : value

// A date rule holding a plain number is a relative window expressed in days, not a calendar date.
const isRelativeDays = (value: SegmentRuleValue): boolean => {
  const scalar = unwrap(value)

  return typeof scalar === 'number' || (typeof scalar === 'string' && /^\d+$/.test(scalar))
}

const isBlank = (value: SegmentRuleValue): boolean => value === null || value === undefined || value === ''

const translateOrFallback = (t: Translate, key: string, fallback: string): string => {
  const translated = t(key)

  return translated === key ? fallback : translated
}

const formatDate = (locale: string, value: string): string => {
  const parsed = dayjs(value)

  if (!parsed.isValid()) return value

  return parsed.locale(locale).format(DATE_ONLY_PATTERN.test(value) ? 'DD/MM/YYYY' : 'DD/MM/YYYY HH:mm')
}

export const getSegmentAttributeLabel = (t: Translate, attribute: string): string => {
  const audienceKey = AUDIENCE_LABEL_KEYS[attribute]

  if (!audienceKey) return attribute

  return translateOrFallback(t, `${AUDIENCE_BLOCK_I18N_PREFIX}.${audienceKey}.label`, attribute)
}

const AFFIRMATIVE_OPERATORS = ['in', 'eq']

export const getSegmentOperatorLabel = (t: Translate, rule: ActivitySegmentRule): string => {
  // The audience recap spells an enum or boolean rule as label + value, with no operator in
  // between. A negation still has to show, otherwise the chip states the opposite of the rule.
  if (AUDIENCE_VALUE_KEYS[rule.attribute] && AFFIRMATIVE_OPERATORS.includes(rule.operator)) return ''

  const key = isDateAttribute(rule.attribute)
    ? `${AUDIENCE_OPERATOR_I18N_PREFIX}.date.${isRelativeDays(rule.value) ? `relatives.${rule.operator}` : rule.operator}`
    : `${AUDIENCE_OPERATOR_I18N_PREFIX}.${rule.operator}`

  // The audience recap renders these labels standalone and capitalizes them; in a chip they sit
  // right after the attribute name, so the leading capital has to go.
  return uncapitalize(translateOrFallback(t, key, rule.operator))
}

const formatScalarValue = (t: Translate, locale: string, attribute: string, value: SegmentRuleScalar): string => {
  const wording = AUDIENCE_VALUE_KEYS[attribute]

  if (wording) {
    const leaf = wording.leaf(value)

    if (leaf) return translateOrFallback(t, `${AUDIENCE_BLOCK_I18N_PREFIX}.${wording.prefix}.${leaf}`, String(value))
  }

  if (isBlank(value)) return EMPTY_VALUE
  if (typeof value === 'boolean') return t(value ? 'global.words.yes' : 'global.words.no')

  if (typeof value === 'string' && (DATE_ONLY_PATTERN.test(value) || DATE_TIME_PATTERN.test(value))) {
    return formatDate(locale, value)
  }

  return String(value)
}

export const formatSegmentValue = (
  t: Translate,
  locale: string,
  attribute: string,
  value: SegmentRuleValue
): string => {
  if (Array.isArray(value)) {
    if (!value.length) return EMPTY_VALUE

    return value.map((entry) => formatScalarValue(t, locale, attribute, entry)).join(', ')
  }

  return formatScalarValue(t, locale, attribute, value)
}

export const formatSegmentRuleValue = (t: Translate, locale: string, rule: ActivitySegmentRule): string => {
  if (isDateAttribute(rule.attribute) && isRelativeDays(rule.value)) {
    return `${unwrap(rule.value)} ${t('global.words.days')}`
  }

  return formatSegmentValue(t, locale, rule.attribute, rule.value)
}

export const buildSegmentConditions = (
  t: Translate,
  locale: string,
  rules: ActivitySegmentRule[] | null | undefined
): SegmentCondition[] =>
  (rules ?? []).map((rule, index) => ({
    key: `${rule.attribute}-${index}`,
    label: getSegmentAttributeLabel(t, rule.attribute),
    operator: getSegmentOperatorLabel(t, rule),
    value: formatSegmentRuleValue(t, locale, rule)
  }))

export const buildSegmentChanges = (
  t: Translate,
  locale: string,
  changedAttributes: Record<string, ActivitySegmentChangedAttribute> | null | undefined
): SegmentChange[] =>
  Object.entries(changedAttributes ?? {}).map(([attribute, change]) => ({
    key: attribute,
    label: getSegmentAttributeLabel(t, attribute),
    before: formatSegmentValue(t, locale, attribute, change?.before ?? null),
    after: formatSegmentValue(t, locale, attribute, change?.after ?? null)
  }))
